import { createFileRoute } from '@tanstack/react-router';
import { lazy, Suspense, useMemo, useRef, useState } from 'react';
import {
  CheckCircle2,
  Eye,
  FlaskConical,
  Mail,
  Pencil,
  Plus,
  Send,
  Trash2,
  TriangleAlert,
  WrapText,
  Zap,
} from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Combobox } from '@/components/ui/Combobox';
import { Dialog } from '@/components/ui/Dialog';
import { Drawer } from '@/components/ui/Drawer';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { Input } from '@/components/ui/Input';
import { RelativeTime } from '@/components/ui/RelativeTime';
import { DataTable } from '@/components/ui/DataTable';
import type { Column } from '@/components/ui/DataTable';
import { SkeletonRows } from '@/components/ui/Skeleton';
import { Tabs } from '@/components/ui/Tabs';

// CodeMirror kostet einen eigenen Chunk - nur laden, wenn der HTML-Tab
// wirklich benutzt wird (Muster PlaybookPanel).
const HtmlCodeMirror = lazy(() => import('@/components/ui/HtmlCodeMirror'));
import { useCustomers } from '@/hooks/useCustomers';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import {
  useCreateMailTemplate,
  useDeleteMailTemplate,
  useMailConfig,
  useMailTemplates,
  useLiveTemplatePreview,
  useSendTestMail,
  useUpdateMailTemplate,
} from '@/hooks/useMail';
import {
  isTestModeActive,
  useMailSettings,
  useSetMailSettings,
} from '@/hooks/useMailSettings';
import { formatDateTime, mailTriggerLabel } from '@/lib/format';
import type { MailHtmlMode, MailTemplate, MailTemplateTrigger } from '@/types/api';

/**
 * /settings/mail - SMTP-Verbindung, Versand-Automatik und Mail-Vorlagen.
 *
 * SMTP-Zugangsdaten kommen aus der ENV (ops/.env) und sind hier bewusst
 * read-only; das Passwort verlässt das Backend nie. Vorlagen werden mit
 * sandboxed Jinja2 gerendert - die Variablen-Whitelist pro Auslöser ist
 * DSGVO-motiviert (keine Hostnames/IPs/Versionen in Kundenmails).
 */

export const Route = createFileRoute('/settings/mail')({
  component: MailSettingsPage,
});

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Fallback-Whitelist pro Auslöser (identisch zur Backend-Definition).
 * Für bestehende Vorlagen liefert das Backend `available_variables` mit -
 * die gewinnen, solange der Auslöser im Editor nicht umgestellt wurde.
 */
// Muss TRIGGER_VARIABLES im Backend (services/mail/templates.py) spiegeln.
export const TRIGGER_VARIABLES: Record<MailTemplateTrigger, string[]> = {
  wartung_gestartet: [
    'kunde',
    'zeitpunkt',
    'wartungsfenster',
    'laeufe_anzahl',
    'systeme_anzahl',
    'routinen',
  ],
  wartung_abgeschlossen: [
    'kunde',
    'zeitpunkt',
    'wartungsfenster',
    'laeufe_anzahl',
    'systeme_anzahl',
    'routinen',
    'dauer',
  ],
  wartung_bericht: [
    'kunde',
    'zeitraum',
    'zeitpunkt',
    'dauer',
    'wartungsfenster',
    'laeufe_anzahl',
    'systeme_anzahl',
    'routinen',
    'arbeiten',
    'ergebnis',
  ],
  wartung_manuell: [
    'kunde',
    'zeitraum',
    'zeitpunkt',
    'dauer',
    'systeme_anzahl',
    'arbeiten',
    'durchgefuehrt_von',
  ],
  run_started: ['kunde', 'routine', 'systeme_anzahl', 'zeitpunkt', 'wartungsfenster'],
  run_finished: ['kunde', 'routine', 'systeme_anzahl', 'status_text', 'dauer', 'zeitpunkt'],
  maintenance_due: ['kunde', 'faellig_am', 'wartungsfenster', 'frequenz'],
  manual: ['kunde', 'zeitpunkt'],
};

/**
 * Auswahlliste des Auslöser-Felds.
 *
 * Als einfaches Array ist das die einzige Trigger-Liste OHNE Compiler-Zwang -
 * ein fehlender Eintrag fällt weder tsc noch einem Test auf, die Vorlage ist
 * im Editor aber nicht mehr auswählbar. Beim Ergänzen eines Triggers gehört
 * diese Liste deshalb ausdrücklich mit dazu (zusammen mit api.ts,
 * format.ts:MAIL_TRIGGER_LABELS und TRIGGER_VARIABLES oben).
 */
export const TRIGGER_OPTIONS: MailTemplateTrigger[] = [
  'wartung_gestartet',
  'wartung_abgeschlossen',
  'wartung_bericht',
  'wartung_manuell',
  'maintenance_due',
  'manual',
  // Nicht mehr ausgelöst, aber wählbar halten: bestehende Vorlagen tragen
  // diese Auslöser, und ein Wert, der nicht in der Liste steht, wird beim
  // ersten Klick ins Select still überschrieben.
  'run_started',
  'run_finished',
];

/** Editor-Teil: Text ist Pflicht, HTML optional (Text bleibt Fallback). */
type BodyPart = 'text' | 'html';
/** Welchen Zweig der Mail die Vorschau gerade zeigt. */
type PreviewPart = 'html' | 'text';

function MailSettingsPage(): JSX.Element {
  const config = useMailConfig();

  return (
    <div className="flex flex-col gap-4">
      <header>
        <h1 className="text-2xl font-semibold">Mail</h1>
        <p className="text-muted">
          SMTP-Verbindung, Versand-Automatik und Vorlagen für Kundenmails.
        </p>
      </header>

      <TestModeCard />

      {config.isLoading && <SkeletonRows rows={3} cols={3} />}
      {config.isError && <ErrorAlert error={config.error} />}

      {config.data && <SmtpCard config={config.data} />}
      {config.data && <AutomationCard config={config.data} />}

      <TemplatesSection />
    </div>
  );
}

// --- Testmodus (DEV-Redirect) ----------------------------------------------

/**
 * Steuert den app_settings-Eintrag 'mail_redirect' via GET/PUT
 * /mail/settings. Der DB-Eintrag gewinnt IMMER über die ENV
 * MAIL_REDIRECT_ALL_TO - Aktivieren/Deaktivieren wirkt sofort, ohne
 * Backend-Neustart. Deaktivieren nur mit LIVE-Bestätigung: danach gehen
 * Mails WIRKLICH an Kunden.
 */
function TestModeCard(): JSX.Element {
  const settings = useMailSettings();
  const save = useSetMailSettings();
  // null = Feld noch nicht angefasst -> gespeicherte Adresse anzeigen.
  const [address, setAddress] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmWord, setConfirmWord] = useState('');

  const data = settings.data;
  const active = isTestModeActive(data);
  const draft = address ?? data?.redirect_address ?? '';
  const draftInvalid = draft !== '' && !EMAIL_RE.test(draft);
  const canSave = draft.trim() !== '' && !draftInvalid;

  const onSaveEnabled = (): void => {
    if (!canSave || save.isPending) return;
    save.mutate(
      { redirect_enabled: true, redirect_address: draft.trim() },
      { onSuccess: () => setAddress(null) }
    );
  };

  const closeConfirm = (): void => {
    setConfirmOpen(false);
    setConfirmWord('');
  };

  const onDeactivate = (): void => {
    if (confirmWord !== 'LIVE' || save.isPending) return;
    save.mutate(
      {
        redirect_enabled: false,
        // Adresse bleibt gespeichert - erneutes Aktivieren ist ein Klick.
        redirect_address: data?.redirect_address ?? (draft.trim() || null),
      },
      {
        onSuccess: () => {
          closeConfirm();
          setAddress(null);
        },
      }
    );
  };

  return (
    <Card
      title={
        <span className="inline-flex items-center gap-2">
          <FlaskConical className="size-4" aria-hidden="true" />
          Testmodus (DEV-Redirect)
        </span>
      }
      description="Leitet ALLE Kundenmails auf eine interne Adresse um. Die Einstellung gilt sofort und gewinnt über die Umgebungsvariable MAIL_REDIRECT_ALL_TO."
      actions={
        active ? (
          <Badge tone="warning" data-testid="testmode-status">
            aktiv
          </Badge>
        ) : (
          <Badge tone="neutral" data-testid="testmode-status">
            aus
          </Badge>
        )
      }
    >
      {settings.isLoading && <SkeletonRows rows={2} cols={2} />}
      {settings.isError && <ErrorAlert error={settings.error} />}

      {data && (
        <div className="flex flex-col gap-3">
          {active ? (
            <div
              role="alert"
              data-testid="mail-testmode-banner"
              className="flex items-start gap-3 rounded-lg border-2 border-warning bg-warning/10 p-4"
            >
              <TriangleAlert
                className="mt-0.5 size-6 shrink-0 text-warning"
                aria-hidden="true"
              />
              <div className="min-w-0">
                <p className="text-base font-semibold text-warning">
                  DEV-/Testmodus aktiv - alle Kundenmails gehen nur an{' '}
                  <strong className="font-mono">{data.redirect_address}</strong>
                </p>
                <p className="mt-1 text-xs text-muted">
                  Es wird KEINE Mail an echte Kunden zugestellt; die
                  Original-Empfaenger stehen nur als Hinweis im Mail-Body.
                </p>
              </div>
            </div>
          ) : (
            <div
              role="status"
              data-testid="mail-testmode-off"
              className="rounded-lg border border-border bg-surface-2/40 p-3 text-sm"
            >
              <p className="font-medium">
                Testmodus aus - Mails gehen an die echten Kunden-Empfänger.
              </p>
              {data.source === 'db' && data.env_address && (
                <p className="mt-1 text-xs text-muted">
                  Die ENV <code className="font-mono">MAIL_REDIRECT_ALL_TO</code> (
                  <span className="font-mono">{data.env_address}</span>) ist gesetzt,
                  wird aber durch diese Einstellung übersteuert - es findet KEIN
                  Redirect statt.
                </p>
              )}
            </div>
          )}

          {active && data.source === 'env' && (
            <p className="text-xs text-muted">
              Quelle: ENV <code className="font-mono">MAIL_REDIRECT_ALL_TO</code>.
              Beim Speichern wandert die Einstellung in die Datenbank und gewinnt
              künftig über die ENV.
            </p>
          )}

          <div className="flex flex-wrap items-start gap-2">
            <div className="w-full max-w-xs">
              <Input
                label="Redirect-Adresse"
                type="email"
                placeholder="z.B. dev@itkfm.at"
                value={draft}
                onChange={(e) => setAddress(e.target.value)}
                error={draftInvalid ? 'Keine gültige E-Mail-Adresse.' : undefined}
              />
            </div>
            {active ? (
              <>
                <Button
                  className="mt-6"
                  loading={save.isPending}
                  disabled={!canSave}
                  onClick={onSaveEnabled}
                >
                  Speichern
                </Button>
                <Button
                  className="mt-6"
                  variant="danger"
                  onClick={() => setConfirmOpen(true)}
                >
                  Deaktivieren …
                </Button>
              </>
            ) : (
              <Button
                className="mt-6"
                loading={save.isPending}
                disabled={!canSave}
                onClick={onSaveEnabled}
              >
                Testmodus aktivieren
              </Button>
            )}
          </div>

          {save.isError && !confirmOpen && <ErrorAlert error={save.error} />}
        </div>
      )}

      <Dialog
        open={confirmOpen}
        onClose={closeConfirm}
        title="Testmodus deaktivieren?"
        description="Der Redirect wird abgeschaltet - der Versand geht wieder an die echten Empfänger."
        footer={
          <>
            <Button variant="secondary" onClick={closeConfirm}>
              Abbrechen
            </Button>
            <Button
              variant="danger"
              loading={save.isPending}
              disabled={confirmWord !== 'LIVE'}
              onClick={onDeactivate}
            >
              Deaktivieren - Mails gehen live
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3 text-sm">
          <p
            role="alert"
            className="flex items-start gap-2 rounded-md border border-danger/40 bg-danger/10 p-3 text-danger"
          >
            <TriangleAlert className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
            <span>
              Mails gehen dann WIRKLICH an Kunden: jede Run-, Ankündigungs- und
              Postausgangs-Mail erreicht ab sofort die echten Empfänger.
            </span>
          </p>
          {data?.env_address && (
            <p className="rounded-md border border-border bg-surface-2/40 p-3 text-xs text-muted">
              Die Umgebungsvariable{' '}
              <code className="font-mono">MAIL_REDIRECT_ALL_TO</code> (
              <span className="font-mono">{data.env_address}</span>) greift danach
              NICHT mehr - diese Einstellung übersteuert sie.
            </p>
          )}
          <Input
            label="Zur Bestätigung das Wort LIVE eingeben"
            value={confirmWord}
            onChange={(e) => setConfirmWord(e.target.value)}
            placeholder="LIVE"
            autoComplete="off"
          />
          {save.isError && <ErrorAlert error={save.error} />}
        </div>
      </Dialog>
    </Card>
  );
}

// --- SMTP-Verbindung -------------------------------------------------------

function SmtpCard({
  config,
}: {
  config: NonNullable<ReturnType<typeof useMailConfig>['data']>;
}): JSX.Element {
  const testMail = useSendTestMail();
  const [recipient, setRecipient] = useState('');

  const recipientInvalid = recipient !== '' && !EMAIL_RE.test(recipient);

  const onSendTest = (): void => {
    if (recipientInvalid) return;
    testMail.mutate({ recipient: recipient === '' ? null : recipient });
  };

  return (
    <Card
      title={
        <span className="inline-flex items-center gap-2">
          <Mail className="size-4" aria-hidden="true" />
          SMTP-Verbindung
        </span>
      }
      description="Konfiguration aus der Umgebung (ops/.env). Das Passwort wird nie angezeigt."
      actions={
        config.configured ? (
          <Badge tone="success" data-testid="smtp-status">
            konfiguriert
          </Badge>
        ) : (
          <Badge tone="danger" data-testid="smtp-status">
            nicht konfiguriert
          </Badge>
        )
      }
    >
      <dl className="grid grid-cols-2 gap-3 text-sm md:grid-cols-4">
        <div>
          <dt className="text-xs uppercase tracking-wider text-muted">Host</dt>
          <dd className="font-mono">{config.smtp_host ?? '–'}</dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wider text-muted">Port</dt>
          <dd className="font-mono">{config.smtp_port}</dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wider text-muted">Absender</dt>
          <dd className="break-all">{config.smtp_from ?? '–'}</dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wider text-muted">Benutzer</dt>
          <dd className="break-all font-mono">{config.smtp_user ?? '–'}</dd>
        </div>
      </dl>

      <div className="mt-4 border-t border-border pt-4">
        <h3 className="text-sm font-semibold">Testmail</h3>
        <p className="mt-1 text-xs text-muted">
          Ohne Angabe geht die Testmail an die Standard-Adresse (Redirect bzw.
          Absender-Postfach).
        </p>
        <div className="mt-2 flex flex-wrap items-start gap-2">
          <div className="w-full max-w-xs">
            <Input
              label="Andere Empfänger-Adresse (optional)"
              type="email"
              placeholder="z.B. office@example.at"
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              error={recipientInvalid ? 'Keine gültige E-Mail-Adresse.' : undefined}
            />
          </div>
          <Button
            className="mt-6"
            leftIcon={<Send className="size-4" />}
            loading={testMail.isPending}
            disabled={!config.configured || recipientInvalid}
            title={!config.configured ? 'SMTP ist nicht konfiguriert.' : undefined}
            onClick={onSendTest}
          >
            Testmail senden
          </Button>
        </div>

        {testMail.isError && <ErrorAlert className="mt-3" error={testMail.error} />}
        {testMail.data && testMail.data.sent && (
          <div
            className="mt-3 flex items-start gap-2 rounded-md border border-success/30 bg-success/5 p-3 text-sm text-success"
            role="status"
          >
            <CheckCircle2 className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
            <span>
              Testmail gesendet an{' '}
              <strong className="font-mono">{testMail.data.effective_recipient}</strong>.
            </span>
          </div>
        )}
        {testMail.data && !testMail.data.sent && (
          <div
            className="mt-3 rounded-md border border-danger/30 bg-danger/5 p-3 text-sm text-danger"
            role="alert"
          >
            <p className="font-medium">
              Versand an {testMail.data.effective_recipient} fehlgeschlagen.
            </p>
            {testMail.data.error && <p className="mt-1">{testMail.data.error}</p>}
          </div>
        )}
      </div>
    </Card>
  );
}

// --- Automatik -------------------------------------------------------------

function AutomationCard({
  config,
}: {
  config: NonNullable<ReturnType<typeof useMailConfig>['data']>;
}): JSX.Element {
  return (
    <Card
      title={
        <span className="inline-flex items-center gap-2">
          <Zap className="size-4" aria-hidden="true" />
          Automatik
        </span>
      }
      description="Read-only - beide Werte werden per Umgebungsvariable gesetzt."
    >
      <dl className="grid grid-cols-1 gap-3 text-sm md:grid-cols-2">
        <div>
          <dt className="text-xs uppercase tracking-wider text-muted">
            Run-Ereignisse automatisch senden
          </dt>
          <dd className="mt-1">
            {config.auto_send_run_events ? (
              <Badge tone="success">aktiv</Badge>
            ) : (
              <Badge tone="neutral">deaktiviert</Badge>
            )}
          </dd>
          <dd className="mt-1 text-xs text-muted">
            Bei &quot;deaktiviert&quot; landen Run-Mails als Entwurf im Postausgang.
            Steuerung: <code className="font-mono">MAIL_AUTO_SEND_RUN_EVENTS</code>
          </dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wider text-muted">
            Ankündigung vor Fälligkeit
          </dt>
          <dd className="mt-1 font-medium">
            {config.announce_days_before}{' '}
            {config.announce_days_before === 1 ? 'Tag' : 'Tage'} vorher
          </dd>
          <dd className="mt-1 text-xs text-muted">
            Steuerung: <code className="font-mono">NOTIFICATION_ANNOUNCE_DAYS_BEFORE</code>
          </dd>
        </div>
      </dl>
    </Card>
  );
}

// --- Vorlagen --------------------------------------------------------------

function TemplatesSection(): JSX.Element {
  const templates = useMailTemplates();
  const deleteTemplate = useDeleteMailTemplate();
  const updateTemplate = useUpdateMailTemplate();

  const [editorOpen, setEditorOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<MailTemplate | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<MailTemplate | null>(null);

  const openEditor = (template: MailTemplate | null): void => {
    setEditTarget(template);
    setEditorOpen(true);
  };

  const confirmDelete = (): void => {
    if (!deleteTarget) return;
    deleteTemplate.mutate(deleteTarget.id, {
      onSuccess: () => setDeleteTarget(null),
    });
  };

  const columns = useMemo((): ReadonlyArray<Column<MailTemplate>> => {
    return [
      {
        id: 'name',
        header: 'Name',
        className: 'font-medium',
        filterValue: (tpl) =>
          [
            tpl.name,
            tpl.code,
            tpl.is_seed ? 'Seed' : '',
            tpl.html_mode === 'custom' ? 'eigenes HTML' : '',
          ]
            .filter((part) => part !== '')
            .join(' '),
        cell: (tpl) => (
          <>
            <span className="flex items-center gap-2">
              {tpl.name}
              {tpl.is_seed && <Badge tone="primary">Seed</Badge>}
              {/* Sichtbar machen, welche Vorlagen dem Text NICHT folgen -
                  bei ihnen bleibt eine Textänderung ohne Wirkung. */}
              {tpl.html_mode === 'custom' && (
                <span title="Der HTML-Teil ist von Hand gepflegt und folgt dem Text nicht.">
                  <Badge tone="warning">eigenes HTML</Badge>
                </span>
              )}
            </span>
            <span className="block font-mono text-xs text-muted">{tpl.code}</span>
          </>
        ),
      },
      {
        id: 'ausloeser',
        header: 'Auslöser',
        filter: 'select',
        filterValue: (tpl) => {
          const label = mailTriggerLabel(tpl.trigger);
          return label === '–' ? '' : label;
        },
        cell: (tpl) => <Badge tone="neutral">{mailTriggerLabel(tpl.trigger)}</Badge>,
      },
      {
        id: 'aktiv',
        header: 'Aktiv',
        filter: 'select',
        filterValue: (tpl) => (tpl.is_active ? 'ja' : 'nein'),
        cell: (tpl) =>
          tpl.is_active ? (
            <Badge tone="success">aktiv</Badge>
          ) : (
            <Badge tone="neutral">inaktiv</Badge>
          ),
      },
      {
        id: 'zuletzt-geaendert',
        header: 'Zuletzt geändert',
        className: 'text-muted',
        filterValue: (tpl) => {
          const label = formatDateTime(tpl.updated_at);
          return label === '–' ? '' : label;
        },
        cell: (tpl) => <RelativeTime value={tpl.updated_at} />,
      },
      {
        id: 'aktionen',
        header: 'Aktionen',
        filter: false,
        headerClassName: 'text-right',
        className: 'text-right',
        cell: (tpl) => (
          <span className="inline-flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<Pencil className="size-4" />}
              onClick={() => openEditor(tpl)}
            >
              Bearbeiten
            </Button>
            <Button
              variant="ghost"
              size="sm"
              loading={
                updateTemplate.isPending &&
                updateTemplate.variables?.templateId === tpl.id
              }
              onClick={() =>
                updateTemplate.mutate({
                  templateId: tpl.id,
                  body: { is_active: !tpl.is_active },
                })
              }
            >
              {tpl.is_active ? 'Deaktivieren' : 'Aktivieren'}
            </Button>
            <span
              title={
                tpl.is_seed
                  ? 'Seed-Vorlage kann nicht gelöscht werden - nur deaktivieren.'
                  : undefined
              }
            >
              <Button
                variant="ghost"
                size="sm"
                disabled={tpl.is_seed}
                aria-label={`Vorlage ${tpl.name} löschen`}
                leftIcon={<Trash2 className="size-4" />}
                onClick={() => setDeleteTarget(tpl)}
              >
                Löschen
              </Button>
            </span>
          </span>
        ),
      },
    ];
  }, [updateTemplate]);

  return (
    <Card
      title="Vorlagen"
      description="Betreff und Text pro Auslöser; der HTML-Teil entsteht daraus, solange niemand eigenes HTML pflegt. Gerendert wird mit einer festen Variablen-Whitelist - Hostnames, IPs und Versionen tauchen in Kundenmails nie auf."
      actions={
        <Button leftIcon={<Plus className="size-4" />} onClick={() => openEditor(null)}>
          Neue Vorlage
        </Button>
      }
    >
      {templates.isLoading && <SkeletonRows rows={4} cols={4} />}
      {templates.isError && <ErrorAlert error={templates.error} />}
      {deleteTemplate.isError && !deleteTarget && (
        <ErrorAlert className="mb-3" error={deleteTemplate.error} />
      )}
      {updateTemplate.isError && (
        <ErrorAlert className="mb-3" error={updateTemplate.error} />
      )}

      {templates.data && templates.data.data.length === 0 && (
        <EmptyState
          icon={<Mail className="size-8" />}
          title="Keine Vorlagen vorhanden"
          description="Lege eine Vorlage an, damit Kundenmails nicht als leerer Text rausgehen."
          actions={
            <Button leftIcon={<Plus className="size-4" />} onClick={() => openEditor(null)}>
              Neue Vorlage
            </Button>
          }
        />
      )}

      {templates.data && templates.data.data.length > 0 && (
        <DataTable
          caption="Mail-Vorlagen"
          columns={columns}
          rows={templates.data.data}
          rowKey={(tpl) => tpl.id}
          storageKey="mail-vorlagen"
          rowMuted={(tpl) => !tpl.is_active}
          rowTestId={(tpl) => `template-row-${tpl.code}`}
        />
      )}

      <TemplateEditorDrawer
        open={editorOpen}
        template={editTarget}
        onClose={() => setEditorOpen(false)}
      />

      <Dialog
        open={deleteTarget != null}
        onClose={() => setDeleteTarget(null)}
        title="Vorlage löschen?"
        {...(deleteTarget
          ? {
              description: `"${deleteTarget.name}" (${deleteTarget.code}) wird endgültig gelöscht.`,
            }
          : {})}
        footer={
          <>
            <Button variant="secondary" onClick={() => setDeleteTarget(null)}>
              Abbrechen
            </Button>
            <Button
              variant="danger"
              loading={deleteTemplate.isPending}
              onClick={confirmDelete}
            >
              Endgültig löschen
            </Button>
          </>
        }
      >
        {deleteTemplate.isError && <ErrorAlert error={deleteTemplate.error} />}
      </Dialog>
    </Card>
  );
}

// --- Vorlagen-Editor -------------------------------------------------------

interface TemplateEditorDrawerProps {
  open: boolean;
  /** null = neue Vorlage. */
  template: MailTemplate | null;
  onClose: () => void;
}

function TemplateEditorDrawer({
  open,
  template,
  onClose,
}: TemplateEditorDrawerProps): JSX.Element {
  // key erzwingt Remount bei Wechsel des Edit-Targets -> frischer Form-State.
  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={template ? `Vorlage bearbeiten: ${template.name}` : 'Neue Vorlage'}
      description={
        template?.is_seed
          ? 'Seed-Vorlage - Inhalt anpassbar, löschen nicht möglich. Betreff und Text sind die Quelle; der HTML-Teil entsteht daraus.'
          : 'Betreff und Text sind die Quelle; der HTML-Teil entsteht daraus.'
      }
      widthClass="max-w-3xl"
    >
      {open && (
        <TemplateEditorForm
          key={template?.id ?? 'new'}
          template={template}
          onClose={onClose}
        />
      )}
    </Drawer>
  );
}

function TemplateEditorForm({
  template,
  onClose,
}: {
  template: MailTemplate | null;
  onClose: () => void;
}): JSX.Element {
  const create = useCreateMailTemplate();
  const update = useUpdateMailTemplate();
  const customers = useCustomers({ limit: 200 });

  const [code, setCode] = useState(template?.code ?? '');
  const [name, setName] = useState(template?.name ?? '');
  const [trigger, setTrigger] = useState<MailTemplateTrigger>(
    template?.trigger ?? 'manual'
  );
  const [subject, setSubject] = useState(template?.subject_template ?? '');
  const [body, setBody] = useState(template?.body_template ?? '');
  const [bodyHtml, setBodyHtml] = useState(template?.body_html_template ?? '');
  const [htmlMode, setHtmlMode] = useState<MailHtmlMode>(template?.html_mode ?? 'auto');
  const [bodyPart, setBodyPart] = useState<BodyPart>('text');
  const [previewPart, setPreviewPart] = useState<PreviewPart>('html');
  const [isActive, setIsActive] = useState(template?.is_active ?? true);
  const [previewCustomerId, setPreviewCustomerId] = useState<string | null>(null);

  const subjectRef = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const htmlRef = useRef<HTMLTextAreaElement>(null);
  const lastFocusRef = useRef<'subject' | 'body' | 'html'>('body');

  const isNew = template == null;
  const saving = create.isPending || update.isPending;
  const saveError = isNew ? create.error : update.error;

  const variables = useMemo(() => {
    if (template && template.trigger === trigger && template.available_variables.length > 0) {
      return template.available_variables;
    }
    return TRIGGER_VARIABLES[trigger];
  }, [template, trigger]);

  const codeInvalid = isNew && code !== '' && !/^[a-z0-9][a-z0-9_-]*$/.test(code);
  // 'Eigenes HTML' ohne HTML-Teil wäre eine Vorlage, die nicht sagen kann,
  // was sie verschickt - das Backend lehnt sie ohnehin mit 422 ab.
  const eigenesHtmlLeer = htmlMode === 'custom' && bodyHtml.trim() === '';
  const canSave =
    name.trim() !== '' &&
    subject.trim() !== '' &&
    body.trim() !== '' &&
    !eigenesHtmlLeer &&
    (!isNew || (code.trim() !== '' && !codeInvalid));

  const insertVariable = (variable: string): void => {
    const token = `{{ ${variable} }}`;
    const target = lastFocusRef.current;
    const el =
      target === 'subject'
        ? subjectRef.current
        : target === 'html'
          ? htmlRef.current
          : bodyRef.current;
    if (!el) return;
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? start;
    const next = `${el.value.slice(0, start)}${token}${el.value.slice(end)}`;
    if (target === 'subject') {
      setSubject(next);
    } else if (target === 'html') {
      setBodyHtml(next);
    } else {
      setBody(next);
    }
    // Fokus + Cursor hinter das eingefügte Token setzen (nach dem Re-Render).
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + token.length;
      el.setSelectionRange(pos, pos);
    });
  };

  const onSave = (): void => {
    if (!canSave || saving) return;
    // Der HTML-Teil bleibt gespeichert, auch wenn gerade der Text die Quelle
    // ist: Umschalten auf 'Eigenes HTML' holt genau diese Fassung zurück.
    const htmlPayload = bodyHtml.trim() === '' ? null : bodyHtml;
    if (isNew) {
      create.mutate(
        {
          code: code.trim(),
          name: name.trim(),
          trigger,
          subject_template: subject,
          body_template: body,
          body_html_template: htmlPayload,
          html_mode: htmlMode,
          is_active: isActive,
        },
        { onSuccess: onClose }
      );
    } else {
      update.mutate(
        {
          templateId: template.id,
          body: {
            name: name.trim(),
            trigger,
            subject_template: subject,
            body_template: body,
            body_html_template: htmlPayload,
            html_mode: htmlMode,
            is_active: isActive,
          },
        },
        { onSuccess: onClose }
      );
    }
  };

  // Die Vorschau folgt der Eingabe von selbst. Der Debounce hält das Tippen
  // flüssig: gerendert wird erst, wenn eine halbe Sekunde nichts mehr kam.
  const entwurf = useDebouncedValue(
    useMemo(
      () => ({
        trigger,
        // Der Name ist die Überschrift im Kopfband des abgeleiteten HTML.
        name,
        subjectTemplate: subject,
        bodyTemplate: body,
        bodyHtmlTemplate: bodyHtml.trim() === '' ? null : bodyHtml,
        htmlMode,
      }),
      [trigger, name, subject, body, bodyHtml, htmlMode]
    ),
    500
  );
  const livePreview = useLiveTemplatePreview(
    template?.id,
    entwurf,
    previewCustomerId,
    // Eine ungespeicherte NEUE Vorlage hat noch keine ID - dort gibt es nichts
    // zu rendern, bis einmal gespeichert wurde.
    !isNew
  );
  const previewResult = livePreview.data ?? null;

  /**
   * Text-Teil aus dem HTML erzeugen.
   *
   * Beide Teile von Hand zu pflegen ist die Falle, in die am 17.08.2026 auch
   * der Betreiber gelaufen ist: er änderte die Grußformel im Text, die Mail
   * zeigte weiter die alte - weil Mailprogramme den HTML-Teil bevorzugen.
   *
   * Bewusst eine simple Umsetzung statt einer Bibliothek: Tags raus, <br> und
   * </p> zu Zeilenumbrüchen, Entities auflösen, Leerzeilen zusammenfassen.
   * Jinja-Ausdrücke ({{ ... }}, {% ... %}) bleiben unangetastet - sie müssen
   * den Weg in den Textteil unbeschadet finden, sonst rendert er nicht mehr.
   */
  const textAusHtml = (html: string): string => {
    const mitUmbruechen = html
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/(p|div|tr|h[1-6]|li)>/gi, '\n')
      .replace(/<\/td>/gi, ' ')
      .replace(/<[^>]+>/g, '');
    // Entities löst der Browser auf, statt sie einzeln aufzuzählen: eine
    // Handliste vergisst zwangsläufig welche (&uuml; und &szlig; fehlten im
    // ersten Anlauf, und ausgerechnet die stehen in "Grüßen"). DOMParser
    // führt dabei kein Script aus - das Dokument ist inaktiv.
    const entschluesselt =
      typeof DOMParser === 'undefined'
        ? mitUmbruechen
        : (new DOMParser().parseFromString(mitUmbruechen, 'text/html').documentElement
            .textContent ?? mitUmbruechen);
    return entschluesselt
      .split('\n')
      .map((z) => z.trim())
      .join('\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  };

  return (
    <div className="flex flex-col gap-4">
      {saveError != null && <ErrorAlert error={saveError} />}

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <Input
          label="Code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          disabled={!isNew}
          placeholder="z.B. wartung_ankuendigung"
          hint={isNew ? 'Eindeutiger technischer Schlüssel.' : 'Code ist nach dem Anlegen fix.'}
          error={codeInvalid ? 'Nur Kleinbuchstaben, Ziffern, - und _ erlaubt.' : undefined}
        />
        <Input
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="z.B. Wartungs-Ankündigung"
        />
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div className="flex flex-col gap-1">
          <label htmlFor="template-trigger" className="text-sm font-medium text-text">
            Auslöser
          </label>
          <select
            id="template-trigger"
            value={trigger}
            onChange={(e) => setTrigger(e.target.value as MailTemplateTrigger)}
            className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            {TRIGGER_OPTIONS.map((t) => (
              <option key={t} value={t}>
                {mailTriggerLabel(t)}
              </option>
            ))}
          </select>
        </div>
        <label className="flex items-center gap-2 pt-6 text-sm">
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
            className="size-4 accent-primary"
          />
          <span>Vorlage aktiv</span>
        </label>
      </div>

      <div>
        <p className="text-sm font-medium text-text">Verfügbare Variablen</p>
        <p className="mt-0.5 text-xs text-muted">
          Klick fügt die Variable an der Cursorposition ein (zuletzt fokussiertes
          Feld). Mehr Daten gibt es bewusst nicht - keine Hostnames, IPs oder
          Versionen in Kundenmails.
        </p>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {variables.map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => insertVariable(v)}
              className="rounded-full border border-primary/30 bg-primary/5 px-2.5 py-1 font-mono text-xs text-primary transition-colors hover:bg-primary/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              {`{{ ${v} }}`}
            </button>
          ))}
        </div>
      </div>

      <Input
        ref={subjectRef}
        label="Betreff"
        value={subject}
        onChange={(e) => setSubject(e.target.value)}
        onFocus={() => {
          lastFocusRef.current = 'subject';
        }}
        placeholder="z.B. Wartung bei {{ kunde }} am {{ zeitpunkt }}"
      />

      <Tabs<BodyPart>
        ariaLabel="Mail-Inhalt"
        active={bodyPart}
        onChange={(part) => {
          setBodyPart(part);
          // Variablen-Chips sollen nach dem Wechsel im sichtbaren Feld landen.
          // Bei abgeleitetem HTML gibt es dort kein Eingabefeld - dann bleibt
          // der Text das Ziel.
          lastFocusRef.current = part === 'html' && htmlMode === 'custom' ? 'html' : 'body';
        }}
        items={[
          { id: 'text', label: 'Text' },
          {
            id: 'html',
            label: 'HTML',
            trailing:
              htmlMode === 'custom' ? (
                <Badge tone="warning">eigenes</Badge>
              ) : (
                <Badge tone="neutral">aus Text</Badge>
              ),
          },
        ]}
      >
        {bodyPart === 'text' ? (
          <div className="flex flex-col gap-1">
            <label htmlFor="template-body" className="text-sm font-medium text-text">
              Text
            </label>
            <p className="text-xs text-muted">
              {htmlMode === 'auto'
                ? 'Die Quelle der Mail. Der HTML-Teil entsteht daraus - was du hier änderst, sehen die Empfänger.'
                : 'Nur der Fallback für Textclients. Sichtbar ist bei den meisten Empfängern der HTML-Teil - den pflegst du gerade selbst.'}
            </p>
            <textarea
              id="template-body"
              ref={bodyRef}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              onFocus={() => {
                lastFocusRef.current = 'body';
              }}
              rows={12}
              placeholder={'Guten Tag,\n\nwir melden uns zur geplanten Wartung ...'}
              className="w-full rounded-md border border-border bg-surface px-3 py-2 font-mono text-sm text-text placeholder:text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            />
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {/* Der Schalter ist die eigentliche Entscheidung dieses Tabs:
                folgt das HTML dem Text, oder pflegt es jemand von Hand? */}
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                data-testid="eigenes-html-schalter"
                checked={htmlMode === 'custom'}
                onChange={(e) => setHtmlMode(e.target.checked ? 'custom' : 'auto')}
                aria-describedby="eigenes-html-erklaerung"
                className="size-4 accent-primary"
              />
              <span className="font-medium">Eigenes HTML pflegen</span>
            </label>
            <p id="eigenes-html-erklaerung" className="pl-6 text-xs text-muted">
              Aus: der HTML-Teil wird aus dem Text erzeugt (Logo, Kopfband,
              Faktentabellen). Eine Textänderung wirkt damit auf die ganze Mail.
            </p>

            {htmlMode === 'auto' ? (
              <div
                className="rounded-md border border-border bg-surface-2/40 p-3 text-xs text-muted"
                data-testid="html-abgeleitet-hinweis"
              >
                Der HTML-Teil kommt aus dem Text-Tab - hier gibt es nichts zu
                tippen. Wie er aussieht, zeigt die Vorschau unten.
                {bodyHtml.trim() !== '' && (
                  <span className="mt-1 block">
                    Eine ältere, von Hand gebaute Fassung ist noch gespeichert:
                    einschalten holt sie unverändert zurück.
                  </span>
                )}
              </div>
            ) : (
              <>
                <div
                  className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/10 p-3 text-xs text-text"
                  data-testid="eigenes-html-warnung"
                >
                  <TriangleAlert className="mt-0.5 size-4 shrink-0 text-warning" aria-hidden="true" />
                  <span>
                    Ab jetzt sind es zwei Fassungen, die du beide pflegen musst.
                    Mailprogramme zeigen den HTML-Teil - eine Änderung im Text
                    kommt beim Empfänger dann NICHT an. Genau daran ist die
                    Grußformel im August zweimal hängen geblieben.
                  </span>
                </div>
                <label htmlFor="template-body-html" className="text-sm font-medium text-text">
                  HTML
                </label>
                <p className="text-xs text-muted">
                  Variablenwerte werden beim Rendern escaped - HTML darf nur aus
                  der Vorlage kommen, nie aus einem Kundennamen.
                </p>
                {/* Farbiger Editor wie beim Playbook - der HTML-Teil ist eine
                    Tabellen-Wüste mit Inline-Styles, in der man die
                    Jinja-Ausdrücke sonst nicht wiederfindet. Lazy geladen, weil
                    CodeMirror einen eigenen Chunk kostet; bis er da ist, steht
                    eine schlichte Textarea mit demselben Wert. */}
                <div
                  onFocus={() => {
                    lastFocusRef.current = 'html';
                  }}
                >
                  <Suspense
                    fallback={
                      <textarea
                        id="template-body-html"
                        ref={htmlRef}
                        value={bodyHtml}
                        onChange={(e) => setBodyHtml(e.target.value)}
                        rows={16}
                        spellCheck={false}
                        className="w-full rounded-md border border-border bg-surface px-3 py-2 font-mono text-sm text-text"
                      />
                    }
                  >
                    <HtmlCodeMirror value={bodyHtml} onChange={setBodyHtml} ariaLabel="HTML" />
                  </Suspense>
                </div>
                {eigenesHtmlLeer && (
                  <p className="text-xs text-danger" data-testid="eigenes-html-leer">
                    Ohne HTML-Teil lässt sich das nicht speichern. Entweder HTML
                    hinterlegen oder den Schalter wieder ausmachen.
                  </p>
                )}
                {bodyHtml.trim() !== '' && (
                  <div className="flex flex-wrap items-center gap-3">
                    <Button
                      variant="secondary"
                      size="sm"
                      leftIcon={<WrapText className="size-4" />}
                      onClick={() => setBody(textAusHtml(bodyHtml))}
                      data-testid="text-aus-html"
                      title="Ersetzt den Text-Teil durch den Inhalt des HTML-Teils."
                    >
                      Text-Teil aus HTML nachziehen
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </Tabs>

      <div className="rounded-lg border border-border bg-surface-2/40 p-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="inline-flex items-center gap-2 text-sm font-semibold">
            <Eye className="size-4" aria-hidden="true" />
            Vorschau
            {livePreview.isFetching && (
              <span className="text-xs font-normal text-muted" data-testid="preview-laedt">
                rendert ...
              </span>
            )}
          </h3>
        </div>
        <p className="mt-0.5 text-xs text-muted">
          {isNew
            ? 'Erst speichern - danach rendert die Vorschau jede Änderung sofort mit.'
            : 'Zeigt laufend, was du gerade tippst - auch ungespeichert.'}
        </p>
        <div className="mt-2 flex flex-wrap items-end gap-2">
          <Combobox
            className="w-full max-w-xs"
            label="Kunde (optional, sonst Beispieldaten)"
            value={previewCustomerId}
            onChange={setPreviewCustomerId}
            options={(customers.data?.data ?? []).map((c) => ({
              value: c.id,
              label: c.name,
            }))}
            placeholder="Beispieldaten"
          />
        </div>
        {livePreview.isError && <ErrorAlert className="mt-2" error={livePreview.error} />}
        {previewResult && (
          <div className="mt-3 rounded-md border border-border bg-surface p-3 text-sm">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="font-medium">{previewResult.subject}</p>
              <span className="flex items-center gap-1.5" data-testid="preview-html-herkunft">
                {previewResult.html_mode === 'custom' ? (
                  <Badge tone="warning">eigenes HTML</Badge>
                ) : (
                  <Badge tone="neutral">HTML aus Text</Badge>
                )}
                {previewResult.used_sample_data && <Badge tone="primary">Beispieldaten</Badge>}
              </span>
            </div>
            {/* Welcher Teil wo landet, muss dastehen. Ohne diesen Satz hält
                man den Text-Tab für "die Mail" und wundert sich, warum eine
                Änderung dort beim Empfänger nicht ankommt. */}
            <p className="mt-1 text-xs text-muted">
              Die Mail geht als multipart/alternative raus: Outlook, Gmail und
              Handy zeigen den HTML-Teil, der Text-Teil ist der Fallback für
              Textclients und Screenreader.
            </p>
            <Tabs<PreviewPart>
              className="mt-2"
              ariaLabel="Vorschau-Teil"
              active={previewPart}
              onChange={setPreviewPart}
              items={[
                { id: 'html', label: 'HTML' },
                { id: 'text', label: 'Text' },
              ]}
            >
              {previewPart === 'html' ? (
                /* sandbox="" (ohne allow-scripts): die Vorschau darf nichts
                   ausführen und nicht navigieren. */
                <iframe
                  title="HTML-Vorschau"
                  data-testid="preview-html-frame"
                  srcDoc={previewResult.body_html}
                  sandbox=""
                  className="h-96 w-full rounded-md border border-border bg-white"
                />
              ) : (
                <pre
                  className="whitespace-pre-wrap font-sans text-text"
                  data-testid="preview-text"
                >
                  {previewResult.body}
                </pre>
              )}
            </Tabs>
          </div>
        )}
      </div>

      <footer className="flex justify-end gap-2 border-t border-border pt-4">
        <Button variant="secondary" onClick={onClose}>
          Abbrechen
        </Button>
        <Button loading={saving} disabled={!canSave} onClick={onSave}>
          Speichern
        </Button>
      </footer>
    </div>
  );
}
